Ext.define('SA.data.Legends', {
    constructor(cfg) {
        Object.assign(this, cfg);

        this.breaksCache = {};
        this.settingsCache = {};
        this.saveQueue = [];
    },

    getBreaks(geographicUnit) {
        this.load(geographicUnit);

        return this.breaksCache[geographicUnit];
    },

    getSettings(geographicUnit) {
        this.load(geographicUnit);

        return this.settingsCache[geographicUnit];
    },

    isLoading() {
        return !!this.loading;
    },

    load(geographicUnit) {
        var breaksCache = this.breaksCache,
            settingsCache = this.settingsCache;

        if (!(geographicUnit in settingsCache)) {
            settingsCache[geographicUnit] = 0;
        }

        if (!(geographicUnit in breaksCache)) {
            breaksCache[geographicUnit] = 0;
        }

        this.processLoadQueue();
    },

    populateBreaks(geographicUnit, legend) {
        this.breaksCache[geographicUnit] = {
            breaks: legend.breaks,
            classificationMethod: legend.classificationMethod,
            colorScheme: legend.colorScheme,
            colors: legend.colors,
            extent: legend.extent,
            numberOfRanges: legend.colors.length
        };
    },

    populateSettings(geographicUnit, legend) {
        var sharedSettings = this.sharedSettings || (this.sharedSettings = {});

        Object.assign(sharedSettings, {
            classificationMethod: legend.classificationMethod,
            extent: legend.extent,
            numberOfRanges: legend.colors.length
        });

        this.settingsCache[geographicUnit] = Object.assign(Object.create(sharedSettings), {
            maximum: legend.maximum,
            minimum: legend.minimum,
            outline: legend.outline
        });

        if (legend.breaks) {
            this.populateBreaks(geographicUnit, legend);
        }
    },

    processLoadQueue() {
        var me = this,
            map = this.map,
            breaksCache = this.breaksCache,
            settingsCache = this.settingsCache,
            attribute = this.attribute,
            customColorsMissing = false,
            geographicUnit, fallbacks;

        this.processSaveQueue();

        if (this.saving || this.isLoading()) {
            return;
        }

        for (let unit in settingsCache) {
            if (settingsCache[unit] === 0 || breaksCache[unit] === 0) {
                geographicUnit = unit;
                break;
            }
        }

        if (!geographicUnit) {
            return;
        }

        if (settingsCache[geographicUnit] === 0) {
            settingsCache[geographicUnit] = false;
        }

        if (breaksCache[geographicUnit] === 0) {
            breaksCache[geographicUnit] = false;
        }

        attemptLoad({});

        async function attemptLoad(overrides) {
            me.setLoading(true);

            var data = Object.assign({}, {
                attribute: me.attribute,
                geographicUnit,
                view: map.getId()
            }, overrides);

            try {
                let legend = await SA.Ajax.request({resource: 'legend', data, expectedErrors: ['UnknownColorSchemeException']});

                me.setLoading(false);

                if (me.attribute === attribute) {
                    var breaks = legend.breaks,
                        fireChangeEvent = true;

                    if (!fallbacks) {
                        me.populateSettings(geographicUnit, legend);
                    }
                    else if (breaks) {
                        me.populateBreaks(geographicUnit, legend);
                    }
                    else {
                        fireChangeEvent = false;
                    }

                    if (!breaks) {
                        if (!fallbacks) {
                            fallbacks = [];

                            [
                                {classificationMethod: 'Quantiles', extent: null},
                                {classificationMethod: 'Natural Breaks', extent: null},
                                {classificationMethod: 'Equal Intervals'}
                            ].forEach(method => {
                                if (method.classificationMethod === legend.classificationMethod) {
                                    if (legend.extent) {
                                        fallbacks.unshift(method);
                                    }
                                }
                                else {
                                    fallbacks.push(method);
                                }
                            });

                            if (customColorsMissing) {
                                fallbacks.forEach(fallback => {
                                    fallback.colorScheme = 'red';
                                });
                            }

                            for (var ranges = legend.colors.length - 1 ; ranges > 1 ; --ranges) {
                                fallbacks.push({classificationMethod: 'Equal Intervals', numberOfRanges: ranges});
                            }
                        }

                        if (fallbacks.length) {
                            attemptLoad(fallbacks.shift());
                        }
                        else {
                            me.breaksCache[geographicUnit] = null;

                            fireChangeEvent = true;
                        }
                    }

                    if (fireChangeEvent) {
                        map.fireEvent('change:legend', map);
                    }
                }

                me.processLoadQueue();
            }
            catch (error) {
                me.setLoading(false);

                if (error.hasType('UnknownColorSchemeException')) {
                    customColorsMissing = true;
                    overrides.colorScheme = 'red';
                    attemptLoad(overrides);
                }
                else {
                    me.processLoadQueue();
                    error.flagUnexpected();
                }
            }
        }
    },

    async processSaveQueue() {
        var me = this,
            queue = this.saveQueue,
            data;

        if (this.saving || this.isLoading() || !(data = queue.shift())) {
            return;
        }

        this.saving = true;

        var map = this.map,
            attribute = data.attribute,
            geographicUnit = data.geographicUnit,
            sharedSettings = this.sharedSettings,
            settingsCache = this.settingsCache,
            settings = settingsCache[geographicUnit],
            breaksCache = this.breaksCache,
            breaks = breaksCache[geographicUnit],
            invalidateAllBreaks = !settings || !breaks,
            newCustomBreaks = data.breaks,
            colors, colorScheme;

        if (data.colors) {
            data.numberOfRanges = data.colors.length;
        }

        if (newCustomBreaks) {
            Ext.iterate({
                breaks: newCustomBreaks,
                classificationMethod: 'custom',
                extent: null,
                numberOfRanges: newCustomBreaks.length + 1
            }, function(prop, value) {
                data[prop] = value;

                if (breaks) {
                    breaks[prop] = value;
                }
            });
        }

        if (sharedSettings) {
            for (let prop in sharedSettings) {
                if (prop in data && data[prop] !== sharedSettings[prop]) {
                    sharedSettings[prop] = data[prop];
                    invalidateAllBreaks = true;
                }
            }
        }

        if (invalidateAllBreaks) {
            breaksCache = this.breaksCache = {};
        }

        if (breaks && newCustomBreaks && breaks.colors.length === data.numberOfRanges) {
            breaksCache[geographicUnit] = breaks;
        }

        breaks = breaksCache[geographicUnit];

        if (breaks) {
            if (data.outline) {
                settings.outline = data.outline;
            }

            if (data.colorScheme && data.colorScheme !== 'custom') {
                colorScheme = data.colorScheme;

                SA.data.ColorScheme.all.forEach(scheme => {
                    if (scheme.name === colorScheme) {
                        colors = scheme[breaks.numberOfRanges].slice();
                    }
                });
            }
            else if (data.colors) {
                colors = data.colors;
                colorScheme = 'custom';
            }

            if (colors) {
                for (let unit in breaksCache) {
                    breaks = breaksCache[unit];

                    if (breaks) {
                        breaks.colors = colors;
                        breaks.colorScheme = colorScheme;
                    }
                }
            }
        }

        map.fireEvent('change:legend', map);

        try {
            await SA.Ajax.request({
                method: 'update',
                resource: 'legend',
                data: Object.assign({view: map.getId()}, data),
                expectedErrors: ['UnknownColorSchemeException'],
                success: onSuccess
            });
        }
        catch (error) {
            this.saving = false;

            if (error.hasType('UnknownColorSchemeException')) {
                onSuccess(null);
                this.processLoadQueue();
                return;
            }

            this.processLoadQueue();
            error.flagUnexpected();
        }

        function onSuccess(legend) {
            me.saving = false;

            if (legend && me.attribute === attribute && (!me.settingsCache[geographicUnit] || !me.breaksCache[geographicUnit])) {
                me.populateSettings(geographicUnit, legend);
                map.fireEvent('change:legend', map);
            }

            me.processLoadQueue();
        }
    },

    set(data) {
        this.saveQueue.push(data);
        this.processSaveQueue();
    },

    setAttribute(attribute) {
        if (this.attribute !== attribute) {
            this.attribute = attribute;
            this.breaksCache = {};
            this.settingsCache = {};
        }
    },

    setLoading(loading) {
        this.loading = loading;

        this.map.fireEvent('change:legendloading');
    }
});